import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { installationApi } from 'services/installationApi';
import type {
  Installation,
  CreateManualInstallationRequest,
  UpdateInstallationRequest,
  VerifyInstallationResult,
} from 'services/installationApi';

export const useCurrentInstallation = () => {
  return useQuery({
    queryKey: ['installations', 'current'],
    queryFn: () => installationApi.getCurrentInstallation(),
    retry: false,
    staleTime: 30000, // 30 seconds
  });
};

export const usePrimaryInstallation = () => {
  return useQuery({
    queryKey: ['installations', 'primary'],
    queryFn: () => installationApi.getPrimaryInstallation(),
    retry: false,
    staleTime: 30000, // 30 seconds
  });
};

export const useDefaultInstallation = () => {
  return useQuery({
    queryKey: ['installations', 'default'],
    queryFn: () => installationApi.getDefaultInstallation(),
    retry: false,
    staleTime: 30000, // 30 seconds
  });
};

export const useWidgetCode = () => {
  return useQuery({
    queryKey: ['installations', 'widget-code'],
    queryFn: () => installationApi.getWidgetCode(),
    retry: false,
    staleTime: 60000, // 1 minute
  });
};

export const useCreateManualInstallation = () => {
  const queryClient = useQueryClient();

  return useMutation<Installation, Error, CreateManualInstallationRequest>({
    mutationFn: (request: CreateManualInstallationRequest) =>
      installationApi.createManualInstallation(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['installations'] });
    },
  });
};

export const useUpdateInstallation = () => {
  const queryClient = useQueryClient();

  return useMutation<Installation, Error, UpdateInstallationRequest>({
    mutationFn: (request: UpdateInstallationRequest) =>
      installationApi.updateInstallation(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['installations'] });
    },
  });
};

export const useDeleteInstallation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (installationId: string) => installationApi.deleteInstallation(installationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['installations'] });
    },
  });
};

export const useSetAsPrimary = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (installationId: string) => installationApi.setAsPrimary(installationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['installations'] });
    },
  });
};

/**
 * VRG-560 — send the install snippet to a typed address.
 *
 * Deliberately NO `onSuccess: invalidateQueries`, unlike every other mutation in
 * this file: nothing on the server changed, so there is nothing to refetch.
 */
export const useSendInstallInstructions = () => {
  return useMutation<{ sent: boolean }, Error, { to: string }>({
    mutationFn: (request: { to: string }) => installationApi.sendInstallInstructions(request),
  });
};

export const useVerifyInstallation = () => {
  const queryClient = useQueryClient();

  return useMutation<VerifyInstallationResult, Error, void>({
    mutationFn: () => installationApi.verifyInstallation(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['installations'] });
    },
  });
};
